import { computed, inject, Injectable, signal } from "@angular/core";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { RECURRING_DETECTOR_PLUGIN_ID } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import {
  AccountOverrides,
  RecurringDetectorService,
} from "./recurring-detector/recurring-detector.service";
import { isRealTransaction } from "./recurring-detector/transaction.utils";

/**
 * Count badges on the "Your tools" tiles (design: a small brand-coloured number on the tile's top
 * right). Only Recurring has one today: how many recurring payments were detected across accounts,
 * honouring the user's "move to recurring / other" overrides from the Recurring page.
 */
@Injectable({ providedIn: "root" })
export class ToolBadgeService {
  private logService = inject(LogService);
  private stateService = inject(StateService);
  private transactionStore = inject(TransactionStoreService);
  private userStore = inject(UserStoreService);
  private detector = inject(RecurringDetectorService);

  private readonly userId = signal<string | undefined>(undefined);
  /** Bumped by refresh() so the count re-reads the Recurring page's overrides from storage. */
  private readonly version = signal(0);

  // Reactive on the transaction/account signals: the stores fill in after the app loads, which can
  // be after this page opens, so a one-off read at init would miss the data.
  private readonly recurringCount = computed(() => {
    this.version();
    try {
      // Overrides are stored per user: wait for the id so we don't count without them and then jump.
      if (!this.userId()) {
        return 0;
      }
      const transactions = this.transactionStore.enrichTransaction.collection();
      const accounts = this.userStore.enrichAccounts.collection();
      if (!transactions || !accounts) {
        return 0;
      }
      const overrides = this.readOverrides();
      // One pass over the transactions, instead of a scan per account.
      const byAccount = new Map<string, typeof transactions>();
      for (const tx of transactions) {
        const id = tx.accountLink?.id;
        if (id && isRealTransaction(tx) && tx.postDate) {
          const list = byAccount.get(id);
          if (list) {
            list.push(tx);
          } else {
            byAccount.set(id, [tx]);
          }
        }
      }
      let total = 0;
      for (const account of accounts) {
        const accountTransactions = byAccount.get(account.id);
        if (!accountTransactions) {
          continue;
        }
        total += this.detector.buildRowData(
          accountTransactions,
          account.id,
          overrides[account.id] ?? {},
        ).matchedRowData.length;
      }
      return total;
    } catch (error) {
      this.logService.error(`Failed to count recurring payments for the tool badge: ${error}`);
      return 0;
    }
  });

  badgeFor(toolId: string): number {
    return toolId === RECURRING_DETECTOR_PLUGIN_ID ? this.recurringCount() : 0;
  }

  /** Call when the page opens: re-reads overrides (and the user id they are stored under). */
  async refresh(): Promise<void> {
    this.userId.set(await this.stateService.getUserId());
    this.version.update((v) => v + 1);
  }

  private readOverrides(): Record<string, AccountOverrides> {
    try {
      const parsed: unknown = JSON.parse(
        localStorage.getItem(`recurring_overrides_v2_${this.userId()}`) || "{}",
      );
      return parsed && typeof parsed === "object"
        ? (parsed as Record<string, AccountOverrides>)
        : {};
    } catch {
      // Corrupt overrides: count without them.
      return {};
    }
  }
}
